import React, { createContext, useContext, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCropPledgeContext } from '../../context/CropPledgeContext';
import { PLEDGE_SECTION_STATUSES, PledgeSectionStatus } from '@bcba/types';
import { BadgeColorScheme } from '@agrotoken-tech/ui';
import { CommonCropPledgeInformationSerializer } from '@bcba/serializers';

import { getCropPledgeInformation } from '../../../../../services/crop-pledge';

type CropPledgeInformationContextType = {
  data?: CommonCropPledgeInformationSerializer;
  isLoading: boolean;
  cropPledgeSectionsStatusBadgeColors: Record<
    (typeof PLEDGE_SECTION_STATUSES)[keyof typeof PLEDGE_SECTION_STATUSES],
    BadgeColorScheme
  >;
  pledgeInformationStatus: PledgeSectionStatus;
};

const CropPledgeInformationContext = createContext<
  CropPledgeInformationContextType | undefined
>(undefined);
type CropPledgeInformationProviderProps = {
  children: ReactNode;
};

export const CropPledgeInformationProvider: React.FC<
  CropPledgeInformationProviderProps
> = ({ children }) => {
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';
  const { cropPledgeSectionsStatusBadgeColors } = useCropPledgeContext();

  const { data, isLoading } = useQuery({
    queryKey: ['get-crop-pledge-information', cropPledgeUuid],
    queryFn: () => getCropPledgeInformation(cropPledgeUuid),
  });

  const pledgeInformationStatus = (data?.status ??
    PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD) as PledgeSectionStatus;

  return (
    <CropPledgeInformationContext.Provider
      value={{
        data,
        isLoading,
        cropPledgeSectionsStatusBadgeColors,
        pledgeInformationStatus,
      }}
    >
      {children}
    </CropPledgeInformationContext.Provider>
  );
};

export const useCropPledgeInformationContext =
  (): CropPledgeInformationContextType => {
    const context = useContext(CropPledgeInformationContext);
    if (context === undefined) {
      throw new Error(
        'useCropPledgeInformationContext must be used within a CropPledgeInformationProvider',
      );
    }
    return context;
  };
